/**
 * WorkspaceGate
 *
 * Wraps the router and, when the current URL is a storage-dependent route
 * (`/projects*` or `/editor*`), blocks it until the user has picked a
 * workspace folder and granted read/write permission:
 *
 *   1. Check handles-db for a saved workspace handle
 *   2. If missing → show splash prompting user to pick a folder
 *   3. If present → queryPermission; if granted, set the active root and
 *      render the children. If revoked, show a Reconnect splash.
 *
 * The landing page (`/`) is not a storage-dependent route — it renders
 * without waiting for the gate, so users see no splash flash on first
 * visit. Navigating into a protected route after the handle is initialized
 * falls through to the "ready" path without additional UI.
 *
 * Also listens for permission-lost signals from fs-primitives and flips
 * back to the Reconnect state mid-session.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ensureKnownWorkspaceForCurrent,
  getWorkspaceHandleRecord,
  isFileSystemAccessSupported,
  queryHandlePermission,
  requestHandlePermission,
  saveWorkspaceHandleRecord,
} from '@/infrastructure/storage/handles-db'
import { onPermissionLost, setWorkspaceRoot } from '@/infrastructure/storage/workspace-fs/root'
import { bootstrapWorkspace } from '@/infrastructure/storage/workspace-fs/bootstrap'
import { MARKER_FILENAME } from '@/infrastructure/storage/workspace-fs/paths'
import { exists } from '@/infrastructure/storage/workspace-fs/fs-primitives'
import { createLogger } from '@/shared/logging/logger'
import { hasActiveSession } from '@/features/auth/stores/auth-session-store'
import { WorkspaceGateSplash } from './workspace-gate-splash'
import { usePathname } from './use-pathname'
import { autoPurgeExpiredTrash } from './deps/trash-auto-purge'

/**
 * Routes that read/write the workspace and therefore need the gate to be
 * ready before their loaders run. Anything else renders freely without
 * waiting on storage initialization.
 */
function isStorageProtectedPath(pathname: string): boolean {
  return pathname.startsWith('/projects') || pathname.startsWith('/editor')
}

const logger = createLogger('WorkspaceGate')

type GateStatus =
  | { kind: 'initializing' }
  | { kind: 'unavailable' } // Non-Chromium browsers
  | { kind: 'pick' } // No saved handle
  | { kind: 'reconnect'; handleName: string } // Saved handle, permission revoked
  | { kind: 'ready' }

export function WorkspaceGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<GateStatus>({ kind: 'initializing' })
  const [busyLabel, setBusyLabel] = useState<string | null>(null)
  const pathname = usePathname()
  const needsWorkspace = isStorageProtectedPath(pathname)
  const isAuthenticated = hasActiveSession()

  const scheduleAutoPurge = useCallback(() => {
    setTimeout(() => {
      void autoPurgeExpiredTrash()
    }, 0)
  }, [])

  const runBootstrap = useCallback(async (handle: FileSystemDirectoryHandle) => {
    try {
      await bootstrapWorkspace(handle)
    } catch (error) {
      logger.warn('bootstrapWorkspace failed', error)
    }
  }, [])

  const activate = useCallback(
    async (
      handle: FileSystemDirectoryHandle,
      options: { deferBootstrap?: boolean } = {},
    ) => {
      const { deferBootstrap = false } = options

    setWorkspaceRoot(handle)
      if (deferBootstrap) {
        setStatus({ kind: 'ready' })
        scheduleAutoPurge()
        setTimeout(() => {
          void runBootstrap(handle)
        }, 0)
        return
      }

      await runBootstrap(handle)
      scheduleAutoPurge()
      setStatus({ kind: 'ready' })
    },
    [runBootstrap, scheduleAutoPurge],
  )

  // Initial load: check if we have a saved handle, check its permission.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!isFileSystemAccessSupported()) {
        if (!cancelled) setStatus({ kind: 'unavailable' })
        return
      }
      // Promote any legacy `workspace:current` into a proper known-workspace
      // record before we read it, so the indicator's "known workspaces" list
      // includes the one the user is about to use.
      await ensureKnownWorkspaceForCurrent()
      const record = await getWorkspaceHandleRecord()
      if (!record) {
        if (!cancelled) setStatus({ kind: 'pick' })
        return
      }
      const handle = record.handle as FileSystemDirectoryHandle
      const permission = await queryHandlePermission(handle)
      if (cancelled) return
      if (permission === 'granted') {
        await activate(handle)
      } else {
        setStatus({ kind: 'reconnect', handleName: record.name })
      }
    })().catch((error) => {
      logger.error('Gate initialization failed', error)
      if (!cancelled) setStatus({ kind: 'pick' })
    })
    return () => {
      cancelled = true
    }
  }, [activate])

  // Permission-lost mid-session → flip to reconnect.
  useEffect(() => {
    const unsubscribe = onPermissionLost(() => {
      void (async () => {
        const record = await getWorkspaceHandleRecord()
        setStatus({ kind: 'reconnect', handleName: record?.name ?? 'workspace' })
      })()
    })
    return () => {
      unsubscribe()
    }
  }, [])

  const handlePick = useCallback(async () => {
    try {
      setBusyLabel('正在选择文件夹...')
      const handle = await window.showDirectoryPicker({
        id: 'freecut-workspace',
        mode: 'readwrite',
        startIn: 'documents',
      })
      setBusyLabel('正在连接工作区...')
      const queryState = await queryHandlePermission(handle)
      const finalState =
        queryState === 'granted' ? queryState : await requestHandlePermission(handle)
      if (finalState !== 'granted') {
        setBusyLabel(null)
        setStatus({ kind: 'reconnect', handleName: handle.name })
        return
      }
      const hasMarker = await exists(handle, [MARKER_FILENAME])
      await saveWorkspaceHandleRecord(handle)
      await activate(handle, { deferBootstrap: !hasMarker })
    } catch (error) {
      setBusyLabel(null)
      if (error instanceof DOMException && error.name === 'AbortError') {
        // User cancelled the picker; stay on splash.
        return
      }
      logger.error('Folder pick failed', error)
    }
  }, [activate])

  const handleReconnect = useCallback(async () => {
    setBusyLabel('正在重新连接工作区...')
    const record = await getWorkspaceHandleRecord()
    if (!record) {
      setBusyLabel(null)
      setStatus({ kind: 'pick' })
      return
    }
    const handle = record.handle as FileSystemDirectoryHandle
    const permission = await requestHandlePermission(handle)
    if (permission === 'granted') {
      await activate(handle)
      return
    }
    setBusyLabel(null)
  }, [activate])

  // Routes that don't touch storage never wait on the gate — no splash, no
  // flash, even on first load while we're checking handles-db.
  if (!needsWorkspace || !isAuthenticated) {
    return <>{children}</>
  }

  if (status.kind === 'ready') {
    return <>{children}</>
  }

  // On protected routes during initialization, render a bare background
  // block so the transition from "checking" to "ready" or "splash" is
  // invisible instead of a logo+spinner flash.
  if (status.kind === 'initializing') {
    return <div className="min-h-screen bg-background" aria-hidden="true" />
  }

  return (
    <WorkspaceGateSplash
      status={status}
      onPickFolder={handlePick}
      onReconnect={handleReconnect}
      busyLabel={busyLabel}
    />
  )
}
